import { useAuthentication, useSecureGraphqlQuery } from '@psyomics/ui-common';
import { LoadingSpinner, ReportError } from '@psyomics/ui-components';
import { useCallback, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  DecodeDirectLinkDocument,
  DecodeDirectLinkQuery,
  DecodeDirectLinkQueryVariables,
} from './generated/direct-link';

type DirectLinkUserFacingErrors = 'InviteNotFound';

type Resolver = (params: object) => void;
const useHandleReport = (): Resolver => {
  const navigate = useNavigate();
  const { organisation } = useAuthentication();
  return useCallback<Resolver>(
    (params) => {
      if (!organisation || !('referralId' in params) || typeof params.referralId !== 'string') return null;

      return navigate(`/account/${organisation}/home/report/${params.referralId}`);
    },
    [navigate, organisation]
  );
};

const useQuery = ({ param, action, ivHex }: { param: string; action: string; ivHex?: string }) => {
  const [{ data, fetching, errorMessage }] = useSecureGraphqlQuery<
    DecodeDirectLinkQuery,
    DecodeDirectLinkQueryVariables,
    DirectLinkUserFacingErrors
  >(
    {
      query: DecodeDirectLinkDocument,
      variables: {
        input: {
          action,
          ivHex,
          param,
        },
      },
    },
    ['InviteNotFound']
  );

  const page = data?.decodeDirectLink?.page;
  const pageParams = useMemo(
    () => (data?.decodeDirectLink?.params ? JSON.parse(data.decodeDirectLink.params) : undefined),
    [data?.decodeDirectLink?.params]
  );

  return {
    page,
    pageParams,
    fetching,
    errorMessage,
  };
};

const useResolvers = () => {
  const handleReport = useHandleReport();
  return useMemo(() => new Map<string, Resolver>([['report', handleReport]]), [handleReport]);
};

const useResolver = (page: string | undefined) => {
  const resolvers = useResolvers();
  return page ? resolvers.get(page) : undefined;
};

const DirectLink = ({
  param,
  action,
  ivHex,
}: {
  readonly param: string;
  readonly action: string;
  readonly ivHex?: string;
}) => {
  const { page, pageParams, fetching, errorMessage } = useQuery({ param, action, ivHex });
  const resolver = useResolver(page);
  useEffect(() => {
    if (resolver) {
      resolver(pageParams);
    }
  }, [resolver, pageParams]);

  if (fetching) return <LoadingSpinner />;
  if (errorMessage) return <ReportError error={errorMessage} />;

  return null;
};

export { DirectLink };
